import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  RotateCcw, Check, ChevronLeft, ChevronRight,
  ArrowRight, ArrowLeft, Skull, ShieldCheck, Crosshair,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  calibrate, fehlerText, frameUrl, getProxyStatus, rebuildProxy,
} from "@/lib/api";
import { TID } from "@/lib/testIds";
import { FIELD_PRESETS, getPreset, fullFieldDims, presetRectDimsFor, worldOffsetFor, presetDisplayDimsFor } from "@/lib/fieldPresets";
import { buildCommittedZone } from "@/lib/addZoneFlow";
import { clickGuidanceFor } from "@/lib/clickGuidance";
import ClickGuidanceDiagram from "./ClickGuidanceDiagram";
import SanityMultiZoneBlock from "./SanityMultiZoneBlock";
import AddZonePanel from "./AddZonePanel";

/**
 * Step 2 — guided calibration in 4 phases:
 *   1) corners      — click the 4 corners of the chosen preset rectangle
 *   2) depth_near   — (optional) draw a metric line into the near endzone
 *   3) depth_far    — (optional) same on the opposite endzone
 *   4) sanity       — review the projected brick reticle + sanity score,
 *                     then confirm or step back to fix any phase.
 *
 * The phases share a single frame loader; each phase keeps its own
 * `at_time_s` so the admin can seek to whatever frame is most legible
 * (e.g. a wide shot for corners, a close-up for the back of the endzone).
 */

const PHASE = {
  CORNERS: "corners",
  DEPTH_NEAR: "depth_near",
  DEPTH_FAR: "depth_far",
  SANITY: "sanity",
  // 🆕 Phase E v8c.2 · Multi-Zone Calibration loop. Operator opts into
  // adding additional 4-point zones AFTER the base sanity passed, so
  // the homography can extrapolate accurately across the entire pitch
  // (not just the calibrated rect). State for the active draft zone
  // lives in `addZone*` below; persisted zones land in `extraZones`.
  ADD_ZONE: "add_zone",
};

// 🆕 v8c.13c (2026-02-28) · Unified clockwise (CW) click-order across
// every polygon in the wizard — base zone, extra zones, AND the
// labels rendered in the side panel's ordered list. The labels carry
// an explicit ①②③④ prefix and ALWAYS read CW starting from the
// top-left visible corner of the calibrated rectangle in the SCREEN.
// This eliminates the legacy "Front-Left/Front-Right/Back-Right/
// Back-Left" naming that operators interpreted as CCW when the
// camera placed the goal-line on the BOTTOM of the screen — the
// resulting matrix inversion was the root cause of the 352 / 422
// "0/0 inliers" reprojection failures.
//
// Math contract: the click ORDER (CW from screen-TL) is the
// invariant; the WORLD-orientation flag `calibratedZoneSide`
// already encodes whether the field-of-play extends to the LEFT or
// RIGHT of the calibrated polygon, so the brick-projection math
// stays well-defined regardless of camera angle.
const LABELS_FULL = [
  "① Eckfahne · OBEN LINKS",
  "② Eckfahne · OBEN RECHTS",
  "③ Eckfahne · UNTEN RECHTS",
  "④ Eckfahne · UNTEN LINKS",
];
const LABELS_EZ = [
  "① Endzonen-Ecke · OBEN LINKS",
  "② Endzonen-Ecke · OBEN RECHTS",
  "③ Endzonen-Ecke · UNTEN RECHTS",
  "④ Endzonen-Ecke · UNTEN LINKS",
];
const DEFAULT_BRICK_M = { Outdoor: 20.0, Beach: 7.0, Indoor: 5.0 };

export default function Step2Calibration({ sequenceId, sequence, onComplete }) {
  const imgRef = useRef(null);
  const containerRef = useRef(null);
  const [imgDims, setImgDims] = useState({ w: 0, h: 0 });
  const [displaySize, setDisplaySize] = useState({ w: 0, h: 0 });

  // ── 720p Proxy lifecycle (polled banner) ─────────────────────────────
  // The backend kicks off proxy encoding right after the upload completes;
  // until it lands, frame-scrubbing crawls against the 1080p original. We
  // surface that state with a top-of-wizard banner so the admin knows
  // exactly when the system flips to "warp speed". Polling stops as soon
  // as we hit a terminal state (ready / failed / null).
  const [proxy, setProxy] = useState(null);          // {status, ready, error, size_bytes, ready_at}
  const [proxyJustReady, setProxyJustReady] = useState(false);
  const proxyStartedAtRef = useRef(null);
  const [proxyElapsedS, setProxyElapsedS] = useState(0);

  useEffect(() => {
    if (!sequenceId) return;
    let cancelled = false;
    let timer;
    const poll = async () => {
      try {
        const s = await getProxyStatus(sequenceId);
        if (cancelled) return;
        if (proxyStartedAtRef.current == null && (s.status === "running" || s.status === "pending")) {
          proxyStartedAtRef.current = Date.now();
        }
        const wasNotReady = proxy && proxy.status !== "ready";
        setProxy(s);
        if (s.status === "ready" && wasNotReady) {
          setProxyJustReady(true);
          setTimeout(() => !cancelled && setProxyJustReady(false), 5000);
        }
        // Stop polling on terminal states. `null` = legacy sequence with no
        // proxy lifecycle at all (predates the pipeline).
        if (!s.status || s.status === "ready" || s.status === "failed") return;
      } catch {
        // Network blip — back off and try again on the next tick.
      }
      if (!cancelled) timer = setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sequenceId]);

  // Live "elapsed" counter — only ticks while encoding is actually running
  // so we don't waste timers on idle sequences. Re-anchors when status flips.
  // Deps are intentionally limited to `proxy?.status` — adding the full
  // `proxy` object would restart the timer on every poll result.
  useEffect(() => {
    if (!proxy || (proxy.status !== "pending" && proxy.status !== "running")) {
      proxyStartedAtRef.current = null;
      setProxyElapsedS(0);
      return;
    }
    if (proxyStartedAtRef.current == null) proxyStartedAtRef.current = Date.now();
    const t = setInterval(() => {
      setProxyElapsedS(Math.floor((Date.now() - proxyStartedAtRef.current) / 1000));
    }, 500);
    return () => clearInterval(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proxy?.status]);

  const onRebuildProxy = async () => {
    try {
      proxyStartedAtRef.current = Date.now();
      setProxyElapsedS(0);
      await rebuildProxy(sequenceId);
      setProxy({ ...(proxy || {}), status: "running", error: null });
    } catch {
      // toast picks up via axios interceptor in api.js if present
    }
  };
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(null);
  const [retryNonce, setRetryNonce] = useState(0);

  const duration = sequence?.duration_s || 30;
  const fmt = sequence?.game_config?.format || "Outdoor";
  const presets = FIELD_PRESETS[fmt] || FIELD_PRESETS.Outdoor;
  const fullFieldDefault = fullFieldDims(fmt);
  // 🆕 2026-02-28 v8 · Honor the Step1 Field-Profile dimensions.
  // Step 1 lets the operator pick a division profile (e.g.
  // OUTDOOR_U14 50×20) AND override the four dimension inputs
  // manually. Those values land on `sequence.game_config.field_*_m`.
  // Step 2 must consume them — otherwise the calibrator solves the
  // homography against 100×37 even when the actual pitch is a 50×20
  // youth field and every downstream pass-length is off by ~50 %.
  // Strict precedence (matches the user's stated requirement #2):
  //   game_config (explicit Step1 choice) → fullFieldDims preset
  //   → never null (downstream calibrator clamps to defaults).
  const gc = sequence?.game_config || {};
  const fullField = {
    length: (typeof gc.field_length_m === "number" && gc.field_length_m > 0)
      ? gc.field_length_m : fullFieldDefault.length,
    width: (typeof gc.field_width_m === "number" && gc.field_width_m > 0)
      ? gc.field_width_m : fullFieldDefault.width,
  };
  const defaultBrickM = (typeof gc.brick_distance_m === "number" && gc.brick_distance_m > 0)
    ? gc.brick_distance_m
    : (DEFAULT_BRICK_M[fmt] ?? 20.0);

  // ── persisted-state restore (allows re-entering Step 2 to fine-tune) ──
  const existing = sequence?.calibration;
  const initialPresetId = (() => {
    const m = existing?.mode;
    if (!m) return "endzone";
    if (m === "full_field") return "hauptspielfeld";
    return m;
  })();

  const [presetId, setPresetId] = useState(initialPresetId);
  const preset = useMemo(() => getPreset(fmt, presetId), [fmt, presetId]);
  // 🆕 Phase E v8c · Resolve rect dims AGAINST THE ACTIVE PROFILE so
  // a U17 (64×25) sequence's endzone preset becomes 10×25 m — not the
  // hard-coded Adult 18×37. Without this, the homography solver is
  // told the calibration rectangle is 18×37 m when it's actually
  // 10×25 m, and every projected pass-length comes out scaled by
  // ~1.5-1.8× (the "short 2.5 m dump shows up as 4+ m" bug).
  const profileRect = useMemo(
    () => presetRectDimsFor(fmt, presetId, gc),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fmt, presetId, gc.field_length_m, gc.field_width_m,
     gc.endzone_depth_m, gc.brick_distance_m],
  );
  const [rectL, setRectL] = useState(existing?.rect_length_m ?? profileRect.length);
  const [rectW, setRectW] = useState(existing?.rect_width_m ?? profileRect.width);
  // When the preset changes (or the active profile changes mid-flow),
  // snap rectL / rectW to the freshly-resolved profile values UNLESS
  // the admin has already typed a manual override (existing.rect_*).
  useEffect(() => {
    if (existing?.rect_length_m == null) setRectL(profileRect.length);
    if (existing?.rect_width_m == null) setRectW(profileRect.width);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileRect.length, profileRect.width]);

  const [points, setPoints] = useState(existing?.image_points || []);

  // Per-phase seek time so each phase loads its own frame.
  const [cornersTime, setCornersTime] = useState(existing?.at_time_s ?? 0);
  const [nearTime, setNearTime] = useState(0);
  const [farTime, setFarTime] = useState(0);
  const [sanityTime, setSanityTime] = useState(existing?.at_time_s ?? 0);

  const _hydrateDepth = (side) => {
    const ln = (existing?.depth_lines || []).find((d) => (d.side || "near") === side);
    return ln ? { pts: ln.image_points || [], meters: ln.meters || 0, at_time_s: ln.at_time_s || 0 } : null;
  };
  const hydNear = useMemo(_hydrateDepth.bind(null, "near"), []);
  const hydFar = useMemo(_hydrateDepth.bind(null, "far"), []);

  const [nearPts, setNearPts] = useState(hydNear?.pts || []);
  const [nearMeters, setNearMeters] = useState(hydNear?.meters || 18.0);
  const [nearEnabled, setNearEnabled] = useState(!!hydNear);

  const [farPts, setFarPts] = useState(hydFar?.pts || []);
  const [farMeters, setFarMeters] = useState(hydFar?.meters || 18.0);
  const [farEnabled, setFarEnabled] = useState(!!hydFar);

  const [brickMeters, setBrickMeters] = useState(
    existing?.brick_distance_m ?? defaultBrickM,
  );
  const [brickSide, setBrickSide] = useState(existing?.brick_side || "near");

  // ── Camera-perspective orientation flag ──────────────────────────────
  // For endzone-style presets (pt0PtAxis = "width") the math layer needs
  // to know which physical endzone of the full pitch the calibrated
  // rectangle represents — otherwise the "Far brick" sanity-test projects
  // BEHIND the back of THIS endzone (off the pitch) instead of toward the
  // OPPOSITE endzone's brick mark inside the field of play.
  //   "right" — calibrated endzone is on the RIGHT side of the camera
  //             frame; field of play extends LEFT in image space.
  //   "left"  — mirror image of the above.
  //   ""      — not yet picked → wizard blocks progress past Phase 1.
  // Persisted on the Calibration document so analytics layers (heatmap,
  // passing-flow-map) can normalise across multiple sequences shot from
  // the same camera angle.
  const [calibratedZoneSide, setCalibratedZoneSide] = useState(
    existing?.calibrated_zone_side || "",
  );
  // Reset the flag whenever the preset switches away from endzone-style
  // (full-field / central-play don't need an orientation) so we don't
  // accidentally persist stale metadata.
  useEffect(() => {
    if (preset.pt0PtAxis !== "width") setCalibratedZoneSide("");
  }, [preset]);

  const [phase, setPhase] = useState(PHASE.CORNERS);
  const [sanityResult, setSanityResult] = useState(null);
  const [calResult, setCalResult] = useState(null);   // full /calibrate response (incl. homography)
  const [dragPos, setDragPos] = useState(null);       // live cursor pos during drag {x,y} in image px
  // Last 2D-Drag override (image-px). Set on mouse-up of the brick reticle
  // drag, sent on both sanity preview and final confirm so the persisted
  // calibration carries the admin's exact 2D pin.
  const [brickImageOverride, setBrickImageOverride] = useState(null);
  const [saving, setSaving] = useState(false);

  // ── 🆕 Phase E v8c.2 · Multi-Zone Calibration state ──────────────────
  // After the base sanity passes the operator can stack extra 4-point
  // zones onto the homography solve so it stays accurate across the
  // entire pitch. `extraZones` holds the persisted, validated zones;
  // `addZone*` is the live draft for the active "Add Zone" pass.
  const [extraZones, setExtraZones] = useState(
    existing?.extra_zones ? [...existing.extra_zones] : []
  );
  const [addZonePresetId, setAddZonePresetId] = useState("");
  const [addZoneSide, setAddZoneSide] = useState("");
  const [addZonePoints, setAddZonePoints] = useState([]);
  const [addZoneTime, setAddZoneTime] = useState(0);
  const addZonePreset = useMemo(
    () => addZonePresetId ? getPreset(fmt, addZonePresetId) : null,
    [fmt, addZonePresetId],
  );

  // Initial frame time for the SANITY phase = corners frame.
  useEffect(() => { setSanityTime(cornersTime); }, [cornersTime]);

  // Snap rect dims to the chosen preset's PROFILE-AWARE values (Phase
  // E v8c); admin can still hand-tune via the dim inputs below.
  useEffect(() => {
    setRectL(profileRect.length);
    setRectW(profileRect.width);
  }, [presetId, profileRect.length, profileRect.width]);

  // ── Active phase time (the frame we currently show) ──────────────────
  const phaseTime = (() => {
    if (phase === PHASE.CORNERS) return cornersTime;
    if (phase === PHASE.DEPTH_NEAR) return nearTime;
    if (phase === PHASE.DEPTH_FAR) return farTime;
    if (phase === PHASE.ADD_ZONE) return addZoneTime;
    return sanityTime;
  })();
  // Sync loadTime when the phase itself changes — otherwise re-entering a
  // phase would re-use the previous phase's stale committed time. We read
  // the per-phase state through a ref so this effect only fires on `phase`
  // flip, not on every scrubber tick (which would defeat the debounce).
  const phaseTimesRef = useRef({ cornersTime, nearTime, farTime, sanityTime, addZoneTime });
  useEffect(() => {
    phaseTimesRef.current = { cornersTime, nearTime, farTime, sanityTime, addZoneTime };
  }, [cornersTime, nearTime, farTime, sanityTime, addZoneTime]);
  useEffect(() => {
    const pt = phaseTimesRef.current;
    const t = phase === PHASE.CORNERS ? pt.cornersTime
            : phase === PHASE.DEPTH_NEAR ? pt.nearTime
            : phase === PHASE.DEPTH_FAR ? pt.farTime
            // 🆕 v8c.11b (2026-02-28) · Explicit ADD_ZONE branch so the
            // frame loader stays in sync with the addZoneTime state.
            // Previously this fell through to `sanityTime`, causing the
            // displayed frame and the slider readout to diverge — a
            // capture saved on the visible frame would persist with the
            // STALE corners-frame timestamp.
            : phase === PHASE.ADD_ZONE ? pt.addZoneTime
            : pt.sanityTime;
    setLoadTime(t);
  }, [phase]);
  const setPhaseTime = (t, opts = {}) => {
    const v = Math.max(0, Math.min(duration, +t.toFixed(2)));
    if (phase === PHASE.CORNERS) setCornersTime(v);
    else if (phase === PHASE.DEPTH_NEAR) setNearTime(v);
    else if (phase === PHASE.DEPTH_FAR) setFarTime(v);
    else if (phase === PHASE.ADD_ZONE) setAddZoneTime(v);
    else setSanityTime(v);
    // Debounce the actual backend frame fetch: discrete button presses
    // (immediate=true) commit instantly; slider drag is throttled to one
    // request after the cursor rests for 120 ms — the server-side analog
    // of `video.fastSeek()`.
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (opts.immediate) {
      setLoadTime(v);
    } else {
      debounceRef.current = setTimeout(() => setLoadTime(v), 120);
    }
  };

  const [src, setSrc] = useState("");
  // Tracks whether the *latest* loadTime's frame has arrived. We deliberately
  // do NOT hide the stale frame while waiting — keeping it visible makes
  // scrubbing feel fluid. A subtle pink pulse (opacity dim) signals "fresh
  // frame loading" without the disorienting flash.
  const [imgLoading, setImgLoading] = useState(false);
  // Slider/scrub debouncing — when the admin drags through a 90-min match,
  // every nudge would otherwise fire an ffmpeg→B2 thumbnail roundtrip,
  // queueing up dozens of stale requests that arrive out of order. We
  // commit the rendered phaseTime to a separate "loadTime" only after the
  // gesture rests for 120 ms (or instantly when the change comes from a
  // discrete button press). This is the server-rendered equivalent of
  // `video.fastSeek()` — both snap to the latest target without waiting
  // for in-flight intermediates. Buttons skip the debounce by calling
  // setPhaseTime with `{immediate: true}`.
  const [loadTime, setLoadTime] = useState(0);
  const debounceRef = useRef(null);
  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  useEffect(() => {
    if (!sequenceId) return;
    setImgError(null);
    setImgLoading(true);
    const base = frameUrl(sequenceId, loadTime);
    setSrc(retryNonce ? `${base}&_=${retryNonce}` : base);
  }, [sequenceId, loadTime, retryNonce]);

  const onImgError = () => { setImgError("Frame load failed — please retry."); setImgLoaded(false); setImgLoading(false); };
  const retryLoad = () => { setImgError(null); setRetryNonce(Date.now()); };
  const onImgLoad = () => {
    const img = imgRef.current;
    if (!img) return;
    setImgDims({ w: img.naturalWidth, h: img.naturalHeight });
    setDisplaySize({ w: img.clientWidth, h: img.clientHeight });
    setImgLoaded(true);
    setImgLoading(false);
  };
  useEffect(() => {
    const ro = new ResizeObserver(() => {
      const img = imgRef.current;
      if (img) setDisplaySize({ w: img.clientWidth, h: img.clientHeight });
    });
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // ── Click handler (routes to the active phase's collection) ──────────
  const handleClick = (e) => {
    const img = imgRef.current;
    if (!img || phase === PHASE.SANITY) return;
    const rect = img.getBoundingClientRect();
    const xRel = (e.clientX - rect.left) / rect.width;
    const yRel = (e.clientY - rect.top) / rect.height;
    const pt = { x: xRel * imgDims.w, y: yRel * imgDims.h };

    if (phase === PHASE.CORNERS) {
      if (points.length >= 4) return;
      setPoints([...points, pt]);
    } else if (phase === PHASE.DEPTH_NEAR) {
      if (nearPts.length >= 2) return;
      setNearPts([...nearPts, pt]);
    } else if (phase === PHASE.DEPTH_FAR) {
      if (farPts.length >= 2) return;
      setFarPts([...farPts, pt]);
    } else if (phase === PHASE.ADD_ZONE) {
      // 🆕 v8c.4 · Multi-zone draft. Click-count limit depends on
      // the active preset's kind:
      //   • area  → 4 single-clicks
      //   • line  → 2 single-clicks
      //   • point → 1 DOUBLE-click (single clicks ignored; see
      //              `handleDoubleClick` below)
      const kind = addZonePreset?.kind || "area";
      if (kind === "point") return;  // doubleclick handler owns this
      const max = kind === "line" ? 2 : 4;
      if (addZonePoints.length >= max) return;
      setAddZonePoints([...addZonePoints, pt]);
    }
  };

  // 🆕 v8c.4 · Double-click handler for POINT presets (1 click only,
  // double-click intent makes it impossible to accidentally place a
  // single-click stray point while panning the seek bar).
  const handleDoubleClick = (e) => {
    if (phase !== PHASE.ADD_ZONE) return;
    if (addZonePreset?.kind !== "point") return;
    const img = imgRef.current;
    if (!img) return;
    const rect = img.getBoundingClientRect();
    const xRel = (e.clientX - rect.left) / rect.width;
    const yRel = (e.clientY - rect.top)  / rect.height;
    const pt = { x: xRel * imgDims.w, y: yRel * imgDims.h };
    // Always replace — a point preset has exactly 1 slot, double-click
    // again to RE-PIN.
    setAddZonePoints([pt]);
  };

  // ── Sanity preview (dry-run) ─────────────────────────────────────────
  // `opts.brickImageOverride` — when the admin 2D-drags the brick reticle
  // we POST the cursor pixel here; the backend persists it verbatim AND
  // derives `brick_field_xy` (true real-world 2D coord) for the Heatmap.
  // `opts.brickSideOverride` / `opts.brickMetersOverride` — when a brick
  // control fires its onChange we receive the NEW value here BEFORE
  // React has re-rendered with the new `brickSide` / `brickMeters`
  // state. Passing the value explicitly side-steps the closure-stale
  // bug where the setTimeout(onChangeBrick, 0) callback would otherwise
  // read the PREVIOUS render's closure and ship the OLD side to the
  // backend, producing the "Near endzone → far-mode field_xy"
  // crosshair-coordinate-inversion symptom the coach reported.
  const runSanityPreview = async (opts = {}) => {
    try {
      setSaving(true);
      // 🆕 v8c.19 (2026-02-28) · Defensive payload-integrity guard.
      // The wizard's 4-corner buffer (`points`) can in rare cases be
      // mutated by a transient state change (e.g. a stale-closure
      // setTimeout fire after the operator navigates back to PHASE.
      // CORNERS to re-click). Surface a clean, actionable toast here
      // so the operator NEVER sees the backend's raw "Exactly 4
      // calibration points required" 400 dump.
      if (!Array.isArray(points) || points.length !== 4) {
        toast.error(
          `Base-Kalibration unvollständig: erwartet 4 Eckpunkte, `
          + `aktuell ${points?.length ?? 0}. Bitte zurück zu Schritt `
          + `CORNERS und alle 4 Ecken neu klicken.`
        );
        setSaving(false);
        return;
      }
      const depthLines = [];
      if (nearEnabled && nearPts.length === 2 && nearMeters > 0) {
        depthLines.push({ image_points: nearPts, meters: nearMeters, side: "near", at_time_s: nearTime });
      }
      if (farEnabled && farPts.length === 2 && farMeters > 0) {
        depthLines.push({ image_points: farPts, meters: farMeters, side: "far", at_time_s: farTime });
      }
      const effectiveBrickSide = opts.brickSideOverride ?? brickSide;
      const effectiveBrickMeters = opts.brickMetersOverride ?? brickMeters;
      const res = await calibrate(sequenceId, {
        image_points: points,
        mode: presetId,
        rect_length_m: rectL,
        rect_width_m: rectW,
        at_time_s: cornersTime,
        field_length_m: fullField.length,
        field_width_m: fullField.width,
        pt0_pt1_axis: preset.pt0PtAxis || "length",
        calibrated_zone_side: calibratedZoneSide || null,
        depth_lines: depthLines,
        brick_distance_m: effectiveBrickMeters,
        brick_side: effectiveBrickSide,
        brick_image_xy: opts.brickImageOverride || undefined,
        image_diag_px: Math.hypot(imgDims.w, imgDims.h) || 2202,
        dry_run: true,
        // 🆕 Phase E v8c.2 · Multi-zone payload — extra rectangles
        // the operator clicked on top of the base after sanity passed.
        extra_zones: extraZones.map((z) => ({
          preset_id: z.preset_id,
          side: z.side || null,
          image_points: z.image_points,
          at_time_s: z.at_time_s,
        })),
      });
      setSanityResult(res.sanity);
      setCalResult(res);
      // 🆕 v8c.18 · Hybrid isolation feedback. The backend may have
      // dropped a panning-noisy extra zone from the joint solve and
      // returned `warnings[]` + `zones_status{}` so the operator can
      // see which zones are now visual-only markers. We:
      //   1) Surface ONE toast per isolated zone (operator gets a
      //      clear narrative even when multiple zones drift).
      //   2) Patch the `extraZones` state with the new `status` so
      //      <SanityMultiZoneBlock/> recolours the chip orange.
      const warnings = Array.isArray(res?.warnings) ? res.warnings : [];
      const statusMap = (res && typeof res.zones_status === "object")
        ? res.zones_status : {};
      if (warnings.length > 0) {
        // De-dup against the previous run's toasts so re-running sanity
        // doesn't spam — but always show fresh isolations.
        warnings.forEach((w) => {
          toast.warning(
            `Zone gespeichert. Aufgrund von Kamerabewegung wird diese Zone `
            + `rein visuell mitgeführt, um Verzerrungen zu vermeiden. (${w})`,
            { duration: 6500 }
          );
        });
      }
      if (Object.keys(statusMap).length > 0) {
        setExtraZones((prev) => prev.map((z) => {
          const key = z.side ? `${z.preset_id}(${z.side})` : z.preset_id;
          const status = statusMap[key];
          return status ? { ...z, status } : { ...z, status: "joint" };
        }));
      }
      setPhase(PHASE.SANITY);
    } catch (err) {
      toast.error(fehlerText(err) || "Sanity preview failed");
    } finally {
      setSaving(false);
    }
  };

  // ── Final confirm (persists) ─────────────────────────────────────────
  const confirmCalibration = async () => {
    try {
      setSaving(true);
      // 🆕 v8c.19 · Same defensive integrity guard as runSanityPreview.
      // confirmCalibration is a destructive write — guard hardest.
      if (!Array.isArray(points) || points.length !== 4) {
        toast.error(
          `Base-Kalibration unvollständig: erwartet 4 Eckpunkte, `
          + `aktuell ${points?.length ?? 0}. Bitte zurück zu Schritt `
          + `CORNERS und alle 4 Ecken neu klicken bevor du speicherst.`
        );
        setSaving(false);
        return;
      }
      const depthLines = [];
      if (nearEnabled && nearPts.length === 2 && nearMeters > 0) {
        depthLines.push({ image_points: nearPts, meters: nearMeters, side: "near", at_time_s: nearTime });
      }
      if (farEnabled && farPts.length === 2 && farMeters > 0) {
        depthLines.push({ image_points: farPts, meters: farMeters, side: "far", at_time_s: farTime });
      }
      const finalRes = await calibrate(sequenceId, {
        image_points: points,
        mode: presetId,
        rect_length_m: rectL,
        rect_width_m: rectW,
        at_time_s: cornersTime,
        field_length_m: fullField.length,
        field_width_m: fullField.width,
        pt0_pt1_axis: preset.pt0PtAxis || "length",
        calibrated_zone_side: calibratedZoneSide || null,
        depth_lines: depthLines,
        brick_distance_m: brickMeters,
        brick_side: brickSide,
        brick_image_xy: brickImageOverride || undefined,
        image_diag_px: Math.hypot(imgDims.w, imgDims.h) || 2202,
        dry_run: false,
        // 🆕 Phase E v8c.2 · Persist the multi-zone calibration so
        // future re-edits restore the zones AND the pass-vector save
        // path can read `cal.extra_zones` for in-bounds checks.
        extra_zones: extraZones.map((z) => ({
          preset_id: z.preset_id,
          side: z.side || null,
          image_points: z.image_points,
          at_time_s: z.at_time_s,
        })),
      });
      // 🆕 v8c.18 · Final-save isolation echo. The persisted payload
      // may have flipped zones to `visual_marker_only` since the last
      // sanity preview (e.g. operator re-added a panning-noisy zone
      // and skipped sanity). Surface the warning so they know what
      // landed in MongoDB.
      const saveWarnings = Array.isArray(finalRes?.warnings) ? finalRes.warnings : [];
      saveWarnings.forEach((w) => toast.warning(
        `Beim Speichern: ${w} (Zone wird visuell mitgeführt.)`,
        { duration: 6500 }
      ));
      toast.success(
        extraZones.length > 0
          ? `Calibration saved · ${preset.labelDe} + ${extraZones.length} extra zone${extraZones.length > 1 ? "s" : ""} · score ${sanityResult?.score ?? "—"}/100`
          : `Calibration saved · ${preset.labelDe} · score ${sanityResult?.score ?? "—"}/100`
      );
      onComplete();
    } catch (err) {
      toast.error(fehlerText(err) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // ── Phase navigation helpers ─────────────────────────────────────────
  const goBack = () => {
    if (phase === PHASE.SANITY) {
      if (farEnabled) setPhase(PHASE.DEPTH_FAR);
      else if (nearEnabled) setPhase(PHASE.DEPTH_NEAR);
      else setPhase(PHASE.CORNERS);
    } else if (phase === PHASE.DEPTH_FAR) setPhase(PHASE.DEPTH_NEAR);
    else if (phase === PHASE.DEPTH_NEAR) setPhase(PHASE.CORNERS);
    else if (phase === PHASE.ADD_ZONE) {
      // Cancel an in-progress draft zone and return to sanity.
      cancelAddZone();
    }
  };

  // ── 🆕 Phase E v8c.2 · Multi-Zone wizard handlers ──────────────────
  const startAddZone = () => {
    // Open the draft. Default to the first preset that *isn't* the base
    // (so the operator sees a meaningful suggestion in the dropdown).
    const baseId = presetId;
    const firstAlt = presets.find((p) => p.id !== baseId && p.status !== "stable")
                   ?? presets.find((p) => p.id !== baseId);
    setAddZonePresetId(firstAlt?.id || baseId);
    setAddZoneSide("");
    setAddZonePoints([]);
    // 🆕 v8c.11b (2026-02-28) · CRITICAL: Inherit the operator's CURRENT
    // scrubbed position (sanityTime — the last frame they were looking
    // at during sanity / scrub) instead of snapping the timeline back
    // to the base corners frame. The previous `setAddZoneTime(cornersTime)`
    // caused captures to persist with the STALE corners-frame timestamp
    // (e.g. t=31s for a zone the operator marked on a t=111s pan).
    setAddZoneTime(sanityTime);
    setPhase(PHASE.ADD_ZONE);
  };

  const cancelAddZone = () => {
    setAddZonePresetId("");
    setAddZoneSide("");
    setAddZonePoints([]);
    setPhase(PHASE.SANITY);
  };

  const commitAddZone = async () => {
    const requiredPoints = (() => {
      const k = addZonePreset?.kind;
      if (k === "point") return 1;
      if (k === "line") return 2;
      return 4;
    })();
    if (addZonePoints.length !== requiredPoints || !addZonePresetId) return;
    const requiresSide = !!addZonePreset?.requiresSide
      || addZonePreset?.pt0PtAxis === "width";
    if (requiresSide && !addZoneSide && addZonePreset?.kind !== "line" && addZonePreset?.kind !== "point") {
      toast.error("Bitte zuerst die Seite (Links / Rechts) wählen.");
      return;
    }
    const newZone = buildCommittedZone({
      presetId: addZonePresetId,
      side: addZoneSide || null,
      points: addZonePoints,
      // 🆕 v8c.11b (2026-02-28) · Persist the EXACT timestamp of the
      // frame the operator is looking at right now (phaseTime resolves
      // to addZoneTime while phase === ADD_ZONE). This guarantees a
      // 1:1 mapping between the canvas pixels they clicked on and the
      // video time recorded — eliminates the "stale 31s for a 111s
      // capture" desync the operator hit in live testing.
      atTimeS: phaseTime,
      kind: addZonePreset?.kind || "area",
    });
    const nextZones = [...extraZones, newZone];
    setExtraZones(nextZones);
    setAddZonePresetId("");
    setAddZoneSide("");
    setAddZonePoints([]);
    setPhase(PHASE.SANITY);
    setTimeout(() => runSanityPreview(), 0);
    toast.success(`Zone hinzugefügt · ${nextZones.length} zusätzliche Zone${nextZones.length > 1 ? "n" : ""}`);
  };

  const removeExtraZone = (idx) => {
    const nextZones = extraZones.filter((_, i) => i !== idx);
    setExtraZones(nextZones);
    // Re-sanity so the score reflects the smaller set.
    setTimeout(() => runSanityPreview(), 0);
  };

  const labelsCorners = presetId === "endzone" ? LABELS_EZ : LABELS_FULL;

  // ── Canvas overlay renderer (clears + draws for current phase) ───────
  const drawOverlay = (c) => {
    if (!c || !displaySize.w || !imgDims.w) return;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    const toScreen = (p) => [(p.x / imgDims.w) * displaySize.w, (p.y / imgDims.h) * displaySize.h];

    if (phase === PHASE.CORNERS) {
      drawPolygon(ctx, points.map(toScreen));
      points.forEach((p, i) => drawHandle(ctx, ...toScreen(p), `${i + 1}`));
    } else if (phase === PHASE.DEPTH_NEAR) {
      // show base polygon faded + the near line being drawn
      drawPolygon(ctx, points.map(toScreen), 0.35);
      drawLine(ctx, nearPts.map(toScreen), "#FFB400");
      nearPts.forEach((p, i) => drawHandle(ctx, ...toScreen(p), i === 0 ? "G" : "B", "#FFB400"));
    } else if (phase === PHASE.DEPTH_FAR) {
      drawPolygon(ctx, points.map(toScreen), 0.35);
      drawLine(ctx, farPts.map(toScreen), "#FF59B2");
      farPts.forEach((p, i) => drawHandle(ctx, ...toScreen(p), i === 0 ? "G" : "B", "#FF59B2"));
    } else if (phase === PHASE.SANITY) {
      drawPolygon(ctx, points.map(toScreen), 0.55);
      // Brick reticle is rendered as a draggable DOM element overlay
      // (see <BrickReticle> below), not via canvas — that lets us hook
      // mouse events for "drag to fine-tune" without canvas hit-testing.
    } else if (phase === PHASE.ADD_ZONE) {
      // 🆕 Multi-zone draft polygon (emerald) — show the base polygon
      // faded for context plus the in-progress new zone in emerald.
      drawPolygon(ctx, points.map(toScreen), 0.25);
      const zonePts = addZonePoints.map(toScreen);
      const kind = addZonePreset?.kind || "area";
      ctx.save();
      ctx.globalAlpha = 0.9;
      ctx.strokeStyle = "rgba(16, 185, 129, 0.95)";  // emerald-500
      ctx.lineWidth = 2;
      if (kind === "area") {
        ctx.beginPath();
        zonePts.forEach((p, i) => { if (i === 0) ctx.moveTo(p[0], p[1]); else ctx.lineTo(p[0], p[1]); });
        if (zonePts.length === 4) {
          ctx.closePath();
          ctx.stroke();
          ctx.fillStyle = "rgba(16, 185, 129, 0.12)";
          ctx.fill();
        } else {
          ctx.stroke();
        }
      } else if (kind === "line" && zonePts.length === 2) {
        // 🆕 v8c.4 · Draw the calibrated line segment as a bold
        // dashed emerald stroke so the operator can verify their
        // 2 endpoints align with the visible field line.
        ctx.setLineDash([8, 4]);
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(...zonePts[0]);
        ctx.lineTo(...zonePts[1]);
        ctx.stroke();
        ctx.setLineDash([]);
      } else if (kind === "line" && zonePts.length === 1) {
        // Single endpoint placed — show a small marker only.
      }
      ctx.restore();
      addZonePoints.forEach((p, i) => drawHandle(ctx, ...toScreen(p), `${i + 1}`, "#10b981"));
    }
  };

  // ── Drag-to-fine-tune brick reticle (2D · X *and* Y free) ────────────
  // The admin grabs the pink crosshair and drops it anywhere on the field.
  // While dragging we project the cursor through H so the tooltip shows
  // the live real-world (X, Y) in metres. On release we POST the cursor
  // pixel as `brick_image_xy` override — the backend persists it verbatim
  // and derives `brick_field_xy` (true 2D coord) for the Heatmap. No
  // snap-back to the centerline anymore.
  const dragRef = useRef({ active: false });
  // Mirror the latest cursor pixel so the mouse-up callback (captured at
  // mouse-down time) sees the FINAL drag value, not a stale closure read.
  const dragPosRef = useRef(null);
  const liveFieldRef = useRef({ x: 0, y: 0 });
  const [liveField, setLiveField] = useState({ x: 0, y: 0 });

  const applyH = (H, x, y) => {
    if (!H) return [0, 0];
    const w = H[2][0] * x + H[2][1] * y + H[2][2];
    return [
      (H[0][0] * x + H[0][1] * y + H[0][2]) / w,
      (H[1][0] * x + H[1][1] * y + H[1][2]) / w,
    ];
  };

  const cursorToImagePx = (clientX, clientY) => {
    const img = imgRef.current;
    if (!img || !imgDims.w) return null;
    const rect = img.getBoundingClientRect();
    return {
      x: ((clientX - rect.left) / rect.width) * imgDims.w,
      y: ((clientY - rect.top) / rect.height) * imgDims.h,
    };
  };

  const onReticleDragStart = (e) => {
    e.preventDefault();
    if (!calResult?.homography) return;
    dragRef.current.active = true;
    const move = (ev) => {
      const px = cursorToImagePx(ev.clientX, ev.clientY);
      if (!px) return;
      dragPosRef.current = px;
      setDragPos(px);
      // Live real-world (X, Y) — purely informational tooltip; the source
      // of truth is the pixel we'll POST on mouse-up.
      const [wx, wy] = applyH(calResult.homography, px.x, px.y);
      liveFieldRef.current = { x: wx, y: wy };
      setLiveField({ x: wx, y: wy });
      // Also update brickMeters so the legacy "Brick @ X m" badge stays
      // in sync; we derive it from the y-coord on the chosen side.
      let dist = brickSide === "far" ? wy - rectL : -wy;
      dist = Math.max(0, +dist.toFixed(1));
      setBrickMeters(dist);
    };
    const up = () => {
      dragRef.current.active = false;
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      // Persist 2D-Drag: re-run sanity preview with the final cursor pixel
      // as the `brick_image_xy` override. The backend keeps that pixel
      // verbatim and emits the new brick_field_xy.
      const finalPx = dragPosRef.current;
      setBrickImageOverride(finalPx);
      runSanityPreview({ brickImageOverride: finalPx });
      // Clear the temporary cursor follow — once sanity returns, the
      // reticle is anchored at the persisted pixel from the server.
      setDragPos(null);
      dragPosRef.current = null;
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  // Resolve the reticle screen position. While dragging we follow the
  // cursor exactly (visually intuitive); otherwise we trust the server's
  // canonical projection from the sanity payload.
  const reticleScreen = (() => {
    if (!displaySize.w || !imgDims.w) return null;
    let imgX, imgY;
    if (dragPos) { imgX = dragPos.x; imgY = dragPos.y; }
    else if (sanityResult?.brick_image_xy) {
      imgX = sanityResult.brick_image_xy.x; imgY = sanityResult.brick_image_xy.y;
    } else return null;
    return {
      left: (imgX / imgDims.w) * displaySize.w,
      top: (imgY / imgDims.h) * displaySize.h,
    };
  })();

  const stepNoun = phase === PHASE.CORNERS ? "Corners"
                 : phase === PHASE.DEPTH_NEAR ? "Near depth line"
                 : phase === PHASE.DEPTH_FAR ? "Far depth line"
                 : phase === PHASE.ADD_ZONE ? "Add Zone"
                 : "Sanity";

  const fps = sequence?.fps || 25;
  const frameStep = 1 / fps;
  const fmtTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toFixed(1).padStart(4, "0")}`;

  return (
    <div data-testid={TID.wizardStep(2)} className="grid lg:grid-cols-5 gap-6">
      {/* ──────────── Proxy-status banner (spans both columns) ──────────── */}
      {proxy && proxy.status && proxy.status !== "ready" && (
        <div
          data-testid="proxy-status-banner"
          className={`lg:col-span-5 px-4 py-2.5 rounded-sm border flex items-center justify-between gap-3 font-mono text-[11px] uppercase tracking-widest ${
            proxy.status === "failed"
              ? "border-destructive/60 bg-destructive/10 text-destructive"
              : "border-pink-400/40 bg-pink-400/5 text-pink-300"
          }`}
        >
          <div className="flex items-center gap-2.5 min-w-0">
            {proxy.status === "failed" ? (
              <Skull className="w-4 h-4 shrink-0" />
            ) : (
              <span className="relative flex h-2 w-2 shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-pink-400 opacity-70" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-pink-400" />
              </span>
            )}
            <span className="truncate">
              {proxy.status === "failed"
                ? <>720p proxy build failed · scrubbing falls back to 1080p original</>
                : <>Encoding 720p proxy · {proxyElapsedS}s elapsed — scrubbing runs on 1080p original until ready</>}
            </span>
          </div>
          {proxy.status === "failed" && (
            <Button
              size="sm" variant="outline"
              onClick={onRebuildProxy}
              data-testid="proxy-rebuild-btn"
              className="h-6 px-2 font-mono text-[10px] uppercase tracking-widest border-destructive/60 text-destructive hover:bg-destructive/20"
            >
              <RotateCcw className="w-3 h-3 mr-1" /> Retry
            </Button>
          )}
        </div>
      )}
      {/* Brief "ready" flash — auto-fades after 5 s via state timer. */}
      {proxyJustReady && (
        <div
          data-testid="proxy-ready-banner"
          className="lg:col-span-5 px-4 py-2.5 rounded-sm border border-emerald-400/50 bg-emerald-400/10 text-emerald-300 flex items-center gap-2.5 font-mono text-[11px] uppercase tracking-widest animate-in fade-in"
        >
          <ShieldCheck className="w-4 h-4 shrink-0" />
          <span>
            Proxy ready ⚡ — Scrubbing turbocharged
            {proxy?.size_bytes ? ` · ${(proxy.size_bytes / 1e6).toFixed(1)} MB` : ""}
          </span>
        </div>
      )}

      {/* ──────────── Video frame + scrubber ──────────── */}
      <div className="lg:col-span-3 space-y-3">
        <div
          ref={containerRef}
          className="relative border border-border rounded-sm overflow-hidden bg-secondary"
          style={{ cursor: phase !== PHASE.SANITY && imgLoaded ? "crosshair" : "default", minHeight: 300 }}
        >
          {src && (
            <img
              ref={imgRef}
              data-testid={TID.calibrationImage}
              src={src}
              alt="frame"
              onLoad={onImgLoad}
              onError={onImgError}
              onClick={handleClick}
              onDoubleClick={handleDoubleClick}
              className={`block w-full h-auto select-none transition-opacity duration-200 ${imgLoaded ? (imgLoading ? "opacity-70" : "opacity-100") : "opacity-0"}`}
              draggable={false}
            />
          )}
          {/* Subtle "fast-seek in progress" pulse — only when we already
              have a stale frame on screen. Replaces the disorienting
              white-flash with a snappy mini indicator in the corner. */}
          {imgLoaded && imgLoading && !imgError && (
            <div
              data-testid="seek-loading-pulse"
              className="absolute top-3 right-3 px-2 py-1 bg-card/95 border border-pink-400/60 rounded-sm font-mono text-[10px] uppercase tracking-widest text-pink-300 flex items-center gap-1.5"
            >
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-pink-400 opacity-70" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-pink-400" />
              </span>
              fast-seek · {fmtTime(loadTime)}
            </div>
          )}
          {!imgLoaded && !imgError && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-secondary tactical-grid">
              <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-primary">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-60" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
                </span>
                Decoding frame via ffmpeg…
              </div>
            </div>
          )}
          {imgError && (
            <div
              data-testid="calibration-frame-error"
              className="absolute inset-0 flex flex-col items-center justify-center bg-secondary tactical-grid px-6 text-center"
            >
              <div className="font-display font-black uppercase text-sm tracking-widest text-destructive mb-2">
                Frame load failed
              </div>
              <div className="font-mono text-[11px] text-muted-foreground max-w-md mb-4 leading-relaxed">
                {imgError}
              </div>
              <Button
                data-testid="calibration-frame-retry"
                size="sm"
                onClick={retryLoad}
                className="bg-primary text-primary-foreground hover:bg-primary/90 font-display font-bold uppercase tracking-widest text-[11px]"
              >
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Retry
              </Button>
            </div>
          )}
          <canvas
            data-testid={TID.calibrationCanvas}
            className="absolute inset-0 pointer-events-none"
            width={displaySize.w}
            height={displaySize.h}
            ref={drawOverlay}
          />

          {/* 🆕 v8c.13c (2026-02-28) · ABSOLUTE CANVAS CLEANSING.
              All textual instruction overlays (Click 1/4, Near line,
              Far line, Add-Zone hint, brick (X,Y) badge) have been
              moved out of the canvas viewport and into the right-hand
              side panel — see CornersPanel / DepthPanel / SanityPanel
              / AddZonePanel each of which already renders the same
              numbered checklist + live status. Keeping the targeting
              matrix clean lets the operator land precise clicks on
              landmarks that USED to be obscured by floating hints. */}


          {/* Draggable Brick reticle (sanity phase only) */}
          {phase === PHASE.SANITY && reticleScreen && (
            <div
              data-testid="brick-reticle-drag-handle"
              onMouseDown={onReticleDragStart}
              className="absolute select-none"
              title="Drag to fine-tune brick distance"
              style={{
                left: reticleScreen.left,
                top: reticleScreen.top,
                width: 64,
                height: 64,
                marginLeft: -32,
                marginTop: -32,
                cursor: dragPos ? "grabbing" : "grab",
                touchAction: "none",
              }}
            >
              <svg viewBox="0 0 64 64" className="absolute inset-0 pointer-events-none">
                {/* outer ring */}
                <circle cx="32" cy="32" r="18" fill="none" stroke="#FF59B2" strokeWidth="2" />
                {/* tick marks */}
                <line x1="32" y1="6" x2="32" y2="20" stroke="#FF59B2" strokeWidth="2" />
                <line x1="32" y1="44" x2="32" y2="58" stroke="#FF59B2" strokeWidth="2" />
                <line x1="6" y1="32" x2="20" y2="32" stroke="#FF59B2" strokeWidth="2" />
                <line x1="44" y1="32" x2="58" y2="32" stroke="#FF59B2" strokeWidth="2" />
                {/* center dot */}
                <circle cx="32" cy="32" r="2.5" fill="#FF59B2" />
                {/* invisible larger hit target */}
                <circle cx="32" cy="32" r="22" fill="rgba(255,89,178,0.001)" className="pointer-events-auto" />
              </svg>
              {/* 🆕 v8c.13 (2026-02-28) · Removed the floating live-
                  coordinate label that USED to render below the brick
                  reticle. It trailed directly next to the cursor and
                  blocked the pixel target the operator was trying to
                  land on. The live drag value now appears in the
                  static top-right badge (see `phase === SANITY` block
                  above). The canvas around the reticle stays clean. */}
            </div>
          )}
        </div>

        {/* Scrubber */}
        <div className="border border-border bg-card rounded-sm p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Frame · phase: <span className="text-primary">{stepNoun}</span>
            </div>
            <div className="font-mono text-xs text-primary tabular-nums">{fmtTime(phaseTime)} / {fmtTime(duration)}</div>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="seek-prev-frame" size="sm" variant="ghost"
                    onClick={() => setPhaseTime(phaseTime - frameStep, { immediate: true })}
                    className="h-8 w-8 p-0 hover:bg-accent hover:text-primary">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button data-testid="seek-minus-5s" size="sm" variant="ghost"
                    onClick={() => setPhaseTime(phaseTime - 5, { immediate: true })}
                    className="h-8 px-2 font-mono text-[10px] hover:bg-accent hover:text-primary">
              -5s
            </Button>
            <Button data-testid="seek-minus-1s" size="sm" variant="ghost"
                    onClick={() => setPhaseTime(phaseTime - 1, { immediate: true })}
                    className="h-8 px-2 font-mono text-[10px] hover:bg-accent hover:text-primary">
              -1s
            </Button>
            <Slider
              data-testid="seek-slider"
              value={[phaseTime]} min={0} max={duration} step={0.05}
              onValueChange={(v) => setPhaseTime(v[0])}
              onValueCommit={(v) => setPhaseTime(v[0], { immediate: true })}
              className="flex-1 mx-2"
            />
            <Button data-testid="seek-plus-1s" size="sm" variant="ghost"
                    onClick={() => setPhaseTime(phaseTime + 1, { immediate: true })}
                    className="h-8 px-2 font-mono text-[10px] hover:bg-accent hover:text-primary">
              +1s
            </Button>
            <Button data-testid="seek-plus-5s" size="sm" variant="ghost"
                    onClick={() => setPhaseTime(phaseTime + 5, { immediate: true })}
                    className="h-8 px-2 font-mono text-[10px] hover:bg-accent hover:text-primary">
              +5s
            </Button>
            <Button data-testid="seek-next-frame" size="sm" variant="ghost"
                    onClick={() => setPhaseTime(phaseTime + frameStep, { immediate: true })}
                    className="h-8 w-8 p-0 hover:bg-accent hover:text-primary">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* ──────────── Side panel — phase-specific ──────────── */}
      <div className="lg:col-span-2 space-y-5">
        <PhaseStepper phase={phase} nearEnabled={nearEnabled} farEnabled={farEnabled} />

        {phase === PHASE.CORNERS && (
          <CornersPanel
            preset={preset} presets={presets} presetId={presetId} setPresetId={setPresetId}
            rectL={rectL} setRectL={setRectL} rectW={rectW} setRectW={setRectW}
            points={points} resetPoints={() => setPoints([])} labels={labelsCorners}
            calibratedZoneSide={calibratedZoneSide} setCalibratedZoneSide={setCalibratedZoneSide}
            onNext={() => setPhase(nearEnabled ? PHASE.DEPTH_NEAR : (farEnabled ? PHASE.DEPTH_FAR : PHASE.SANITY))}
            onNextSanity={runSanityPreview}
            saving={saving}
            nearEnabled={nearEnabled} setNearEnabled={setNearEnabled}
            farEnabled={farEnabled} setFarEnabled={setFarEnabled}
            fmt={fmt} fieldProfile={gc}
          />
        )}
        {phase === PHASE.DEPTH_NEAR && (
          <DepthPanel
            side="near"
            pts={nearPts} setPts={setNearPts}
            meters={nearMeters} setMeters={setNearMeters}
            enabled={nearEnabled} setEnabled={setNearEnabled}
            onBack={goBack}
            onNext={() => setPhase(farEnabled ? PHASE.DEPTH_FAR : PHASE.SANITY)}
            onNextSanity={runSanityPreview}
            saving={saving}
            nextIsSanity={!farEnabled}
          />
        )}
        {phase === PHASE.DEPTH_FAR && (
          <DepthPanel
            side="far"
            pts={farPts} setPts={setFarPts}
            meters={farMeters} setMeters={setFarMeters}
            enabled={farEnabled} setEnabled={setFarEnabled}
            onBack={goBack}
            onNext={runSanityPreview}
            saving={saving}
            nextIsSanity={true}
          />
        )}
        {phase === PHASE.SANITY && (
          <SanityPanel
            sanity={sanityResult}
            preset={preset} rectL={rectL} rectW={rectW}
            nearEnabled={nearEnabled} nearMeters={nearMeters}
            farEnabled={farEnabled} farMeters={farMeters}
            brickMeters={brickMeters} setBrickMeters={setBrickMeters}
            brickSide={brickSide} setBrickSide={setBrickSide}
            onChangeBrick={runSanityPreview}
            onBack={goBack}
            onConfirm={confirmCalibration}
            saving={saving}
            extraZones={extraZones}
            onAddZone={startAddZone}
            onRemoveZone={removeExtraZone}
            fmt={fmt}
            basePresetId={presetId}
            baseSide={calibratedZoneSide}
            fieldProfile={gc}
            liveField={liveField}
            dragActive={!!dragPos}
          />
        )}
        {phase === PHASE.ADD_ZONE && (
          <AddZonePanel
            fmt={fmt}
            allPresets={presets}
            basePresetId={presetId}
            baseSide={calibratedZoneSide}
            baseAtTime={cornersTime}
            currentTime={phaseTime}
            extraZones={extraZones}
            zonePresetId={addZonePresetId}
            setZonePresetId={setAddZonePresetId}
            zoneSide={addZoneSide}
            setZoneSide={setAddZoneSide}
            points={addZonePoints}
            resetPoints={() => setAddZonePoints([])}
            zoneCount={extraZones.length}
            onCancel={cancelAddZone}
            onConfirm={commitAddZone}
            saving={saving}
            fieldProfile={gc}
          />
        )}
      </div>
    </div>
  );
}

/* ═════════════════ Canvas drawing primitives ═════════════════ */

function drawPolygon(ctx, screenPts, alpha = 1) {
  if (screenPts.length < 2) return;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = "rgba(0, 230, 150, 0.9)";
  ctx.lineWidth = 2;
  ctx.beginPath();
  screenPts.forEach((p, i) => { if (i === 0) ctx.moveTo(p[0], p[1]); else ctx.lineTo(p[0], p[1]); });
  if (screenPts.length === 4) ctx.closePath();
  ctx.stroke();
  if (screenPts.length === 4) {
    ctx.fillStyle = "rgba(0, 230, 150, 0.12)";
    ctx.fill();
  }
  ctx.restore();
}

function drawLine(ctx, screenPts, color = "#FFB400") {
  if (screenPts.length < 2) return;
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = 3;
  ctx.setLineDash([4, 4]);
  ctx.beginPath();
  ctx.moveTo(screenPts[0][0], screenPts[0][1]);
  ctx.lineTo(screenPts[1][0], screenPts[1][1]);
  ctx.stroke();
  ctx.restore();
}

function drawHandle(ctx, x, y, label, color = "#00e6a0") {
  ctx.save();
  ctx.fillStyle = color; ctx.strokeStyle = "#001a10"; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.arc(x, y, 8, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
  ctx.fillStyle = "#001a10";
  ctx.font = "bold 10px ui-monospace, monospace";
  ctx.textAlign = "center"; ctx.textBaseline = "middle";
  ctx.fillText(String(label), x, y + 0.5);
  ctx.restore();
}

function drawReticle(ctx, x, y) {
  ctx.save();
  ctx.strokeStyle = "#FF59B2"; ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(x, y, 16, 0, Math.PI * 2);
  ctx.moveTo(x - 22, y); ctx.lineTo(x - 6, y);
  ctx.moveTo(x + 6, y); ctx.lineTo(x + 22, y);
  ctx.moveTo(x, y - 22); ctx.lineTo(x, y - 6);
  ctx.moveTo(x, y + 6); ctx.lineTo(x, y + 22);
  ctx.stroke();
  ctx.fillStyle = "#FF59B2"; ctx.beginPath(); ctx.arc(x, y, 2.5, 0, Math.PI * 2); ctx.fill();
  ctx.restore();
}

/* ═════════════════ Phase stepper header ═════════════════ */

function PhaseStepper({ phase, nearEnabled, farEnabled }) {
  const steps = [
    { id: PHASE.CORNERS, label: "Corners", n: 1 },
    { id: PHASE.DEPTH_NEAR, label: "Near Depth", n: 2, optional: !nearEnabled },
    { id: PHASE.DEPTH_FAR, label: "Far Depth", n: 3, optional: !farEnabled },
    { id: PHASE.SANITY, label: "Sanity", n: 4 },
  ];
  const idx = steps.findIndex((s) => s.id === phase);
  return (
    <div className="flex items-center justify-between gap-1 font-mono text-[10px] uppercase tracking-widest">
      {steps.map((s, i) => {
        const active = s.id === phase;
        const done = i < idx;
        return (
          <div key={s.id} className="flex items-center gap-1 flex-1">
            <div className={`w-5 h-5 rounded-sm border flex items-center justify-center ${active ? "bg-primary text-primary-foreground border-primary" : done ? "bg-primary/30 border-primary/40 text-primary" : "bg-secondary border-border text-muted-foreground"}`}>
              {s.n}
            </div>
            <div className={`text-[10px] truncate ${active ? "text-primary" : "text-muted-foreground"}`}>
              {s.label}{s.optional && <span className="opacity-60"> · opt</span>}
            </div>
            {i < steps.length - 1 && <ChevronRight className="w-3 h-3 text-muted-foreground" />}
          </div>
        );
      })}
    </div>
  );
}

/* ═════════════════ Phase 1 — Corners panel ═════════════════ */

function CornersPanel({
  preset, presets, presetId, setPresetId,
  rectL, setRectL, rectW, setRectW,
  points, resetPoints, labels,
  calibratedZoneSide, setCalibratedZoneSide,
  onNext, onNextSanity, saving,
  nearEnabled, setNearEnabled, farEnabled, setFarEnabled,
  // 🆕 v8c.8 · Active match profile so the dropdown dimension labels
  // render against U17 / Adult / Indoor metrics instead of the static
  // Adult-default registry values.
  fmt = "Outdoor", fieldProfile,
}) {
  // Endzone-style presets (`pt0PtAxis === "width"`) demand a camera-side
  // orientation declaration BEFORE the corner click order is committed.
  // Full-field / central-play presets don't need it (the rectangle covers
  // both endzones so "field of play" is unambiguous).
  const needsZoneSide = preset.pt0PtAxis === "width";
  const zoneSidePicked = !needsZoneSide || !!calibratedZoneSide;
  const canAdvance = points.length === 4 && !saving && zoneSidePicked;
  return (
    <div className="border border-border bg-card rounded-sm p-5">
      <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
        Phase 1 · Field Calibration
      </div>
      <h3 className="font-display font-black uppercase text-lg tracking-wider mb-3">
        4-Point Homography
      </h3>

      <div className="mb-4">
        <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Calibration Rectangle
        </Label>
        <Select value={presetId} onValueChange={setPresetId}>
          <SelectTrigger data-testid="cal-preset-select" className="mt-1.5 bg-secondary border-border focus:border-primary">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {/* 🆕 v8c.7 (2026-02-28) · BUG FIX: the BASE 4-point homography
                phase MUST only accept "area" (4-corner rect) presets. Line
                + point presets are degenerate rectangles (width=0 or
                length=0) that collapse cv2.getPerspectiveTransform into
                an unsolvable singular matrix → grotesque pass-lengths
                (48 m on a 25 m-wide field). Line and Point presets remain
                fully available as MULTI-ZONE extras via AddZonePanel. */}
            {presets
              .filter((p) => (p.kind || "area") === "area")
              .map((p) => {
                // 🆕 v8c.8 · Profile-aware label: U17 endzone reads
                // "10×25 m" instead of the Adult registry default
                // "18×37 m". Source of truth = active match's gc.
                const dimLabel = presetDisplayDimsFor(fmt, p.id, fieldProfile);
                return (
                  <SelectItem key={p.id} value={p.id} data-testid={`cal-preset-${p.id}`}>
                    <div className="flex items-center justify-between gap-3 w-full">
                      <span className="font-medium">{p.labelDe}</span>
                      <span className="font-mono text-[10px] text-muted-foreground tabular-nums">
                        {dimLabel}
                      </span>
                    </div>
                  </SelectItem>
                );
              })}
          </SelectContent>
        </Select>
        <div className="mt-2 font-mono text-[10px] text-muted-foreground leading-relaxed">
          💡 Bei Kameraschwenks: kleineres Teil-Viereck wählen — die echten
          Meter-Werte reichen, um die Homographie zu rechnen.
        </div>
      </div>

      {/* Camera-perspective orientation — endzone-style presets only.
          Without this the brick-test math can't tell whether the field of
          play extends to the LEFT or RIGHT of the calibrated polygon,
          which made `Far endzone` project off the physical pitch. */}
      {needsZoneSide && (
        <div className="mb-4 border border-amber-400/30 bg-amber-400/5 rounded-sm p-3">
          <Label className="font-mono text-[10px] uppercase tracking-widest text-amber-400 flex items-center gap-1.5">
            <Crosshair className="w-3 h-3" />
            Kamera-Perspektive · Pflichtfeld
          </Label>
          <Select
            value={calibratedZoneSide || undefined}
            onValueChange={setCalibratedZoneSide}
          >
            <SelectTrigger
              data-testid="cal-zone-side-select"
              className={`mt-1.5 bg-secondary border-border focus:border-primary ${zoneSidePicked ? "" : "ring-1 ring-amber-400/60"}`}
            >
              <SelectValue placeholder="Wähle: linke oder rechte Endzone im Bild" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="left" data-testid="cal-zone-side-left">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[10px] text-muted-foreground">←</span>
                  <span>Linke Endzone <span className="text-muted-foreground">(im Bild links)</span></span>
                </div>
              </SelectItem>
              <SelectItem value="right" data-testid="cal-zone-side-right">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[10px] text-muted-foreground">→</span>
                  <span>Rechte Endzone <span className="text-muted-foreground">(im Bild rechts)</span></span>
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
          <div className="mt-2 font-mono text-[10px] text-muted-foreground leading-relaxed">
            Diese Angabe sagt dem Brick-Test, in welche Richtung das Spielfeld
            verläuft. Ohne sie projiziert &bdquo;Brick der gegenüber. Endzone&ldquo;
            hinter die Backline statt ins Feldinnere.
          </div>
        </div>
      )}

      <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
        {preset.hint} Click corners clockwise: <span className="text-primary">{labels.join(" → ")}</span>.
      </p>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div>
          <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Length (m)</Label>
          <Input data-testid="cal-rect-length" type="number" step="0.1" min="0.1"
            value={rectL} onChange={(e) => setRectL(parseFloat(e.target.value) || 0)}
            className="mt-1 bg-secondary border-border focus:border-primary font-mono" />
        </div>
        <div>
          <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Width (m)</Label>
          <Input data-testid="cal-rect-width" type="number" step="0.1" min="0.1"
            value={rectW} onChange={(e) => setRectW(parseFloat(e.target.value) || 0)}
            className="mt-1 bg-secondary border-border focus:border-primary font-mono" />
        </div>
      </div>

      {/* 🆕 v8c.13c (2026-02-28) · CornersPanel — same click-guidance
          system as AddZonePanel. Locks CW-from-top-left across the
          ENTIRE wizard (base + extras) so the joint DLT solver gets
          a uniform polygon-orientation contract, eliminating the
          legacy CCW vs CW matrix-inversion class of 422 errors. */}
      {(() => {
        const baseGuidance = clickGuidanceFor(preset, { side: calibratedZoneSide });
        if (!baseGuidance) return null;
        return (
          <div
            data-testid="cal-base-zone-guidance"
            className="border-l-4 border-amber-400 bg-amber-400/10 rounded-sm p-3 flex items-start gap-3 mb-4"
          >
            <div className="flex-1 min-w-0">
              <div
                data-testid="cal-base-zone-guidance-headline"
                className="font-display font-black text-amber-200 text-sm leading-tight"
              >
                {baseGuidance.headline}
              </div>
              <div
                data-testid="cal-base-zone-guidance-body"
                className="mt-1.5 text-foreground/90 text-[13px] leading-relaxed"
              >
                {baseGuidance.body}
              </div>
            </div>
            {baseGuidance.diagram && (
              <ClickGuidanceDiagram diagram={baseGuidance.diagram} />
            )}
          </div>
        );
      })()}


      <ol className="space-y-1.5 mb-4">
        {labels.map((l, i) => {
          const set = !!points[i];
          return (
            <li key={l} className="flex items-center gap-3 text-sm">
              <span className={`w-5 h-5 rounded-sm border flex items-center justify-center font-mono text-[10px] ${set ? "bg-primary text-primary-foreground border-primary" : "bg-secondary border-border text-muted-foreground"}`}>{i + 1}</span>
              <span className={set ? "text-foreground" : "text-muted-foreground"}>{l}</span>
              {set && <span className="ml-auto font-mono text-[10px] text-muted-foreground">{Math.round(points[i].x)}, {Math.round(points[i].y)}</span>}
            </li>
          );
        })}
      </ol>

      {/* Optional depth-line toggles */}
      <div className="border-t border-border pt-3 mb-4 space-y-2">
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
          Optional · Tiefen-Linien (für mehr Präzision)
        </div>
        <label className="flex items-center gap-2 cursor-pointer text-xs">
          <input type="checkbox" data-testid="cal-toggle-near"
            checked={nearEnabled} onChange={(e) => setNearEnabled(e.target.checked)}
            className="w-4 h-4 accent-amber-400" />
          <span className="text-amber-400 font-medium">Phase 2 ·</span>
          <span className="text-muted-foreground">Nähere Endzonen-Tiefe (Goalline → Backline)</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer text-xs">
          <input type="checkbox" data-testid="cal-toggle-far"
            checked={farEnabled} onChange={(e) => setFarEnabled(e.target.checked)}
            className="w-4 h-4 accent-pink-400" />
          <span className="text-pink-400 font-medium">Phase 3 ·</span>
          <span className="text-muted-foreground">Gegenüberliegende Endzonen-Tiefe</span>
        </label>
      </div>

      <div className="flex gap-2">
        <Button data-testid={TID.resetCalibrationBtn} variant="outline" onClick={resetPoints}
          className="flex-1 border-border bg-secondary hover:bg-accent text-foreground">
          <RotateCcw className="w-4 h-4 mr-2" /> Reset
        </Button>
        {(nearEnabled || farEnabled) ? (
          <Button data-testid="cal-next-depth" onClick={onNext}
            disabled={!canAdvance}
            className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 font-display font-bold uppercase tracking-widest">
            Next <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button data-testid="cal-next-sanity" onClick={onNextSanity}
            disabled={!canAdvance}
            className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 font-display font-bold uppercase tracking-widest">
            Sanity Check <ShieldCheck className="w-4 h-4 ml-2" />
          </Button>
        )}
      </div>
    </div>
  );
}

/* ═════════════════ Phase 2/3 — Depth line panel ═════════════════ */

function DepthPanel({
  side, pts, setPts, meters, setMeters, enabled, setEnabled,
  onBack, onNext, onNextSanity, saving, nextIsSanity,
}) {
  const color = side === "near" ? "amber-400" : "pink-400";
  const titleDe = side === "near" ? "Nähere Endzone" : "Gegenüberliegende Endzone";
  return (
    <div className="border border-border bg-card rounded-sm p-5">
      <div className={`font-mono text-[10px] uppercase tracking-widest text-${color} mb-1`}>
        Phase {side === "near" ? 2 : 3} · Endzonen-Tiefe (optional)
      </div>
      <h3 className="font-display font-black uppercase text-lg tracking-wider mb-3">{titleDe}</h3>

      <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
        Ziehe eine Linie auf einem Standbild (du kannst zu einem anderen Frame
        skippen!) von der <strong className={`text-${color}`}>Goalline</strong> zur
        <strong className={`text-${color}`}> Endzonen-Backline</strong>.
        Trage rechts die echte Länge in Metern ein.
      </p>

      <ol className="space-y-1.5 mb-4">
        {[0, 1].map((i) => {
          const set = !!pts[i];
          const lbl = i === 0 ? "Goal-line end" : "Back-line end";
          return (
            <li key={lbl} className="flex items-center gap-3 text-sm">
              <span className={`w-5 h-5 rounded-sm border flex items-center justify-center font-mono text-[10px] ${set ? `bg-${color} text-black border-${color}` : "bg-secondary border-border text-muted-foreground"}`}>{i + 1}</span>
              <span className={set ? "text-foreground" : "text-muted-foreground"}>{lbl}</span>
              {set && <span className="ml-auto font-mono text-[10px] text-muted-foreground">{Math.round(pts[i].x)}, {Math.round(pts[i].y)}</span>}
            </li>
          );
        })}
      </ol>

      <div className="mb-4">
        <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Echte Länge der Endzone (m)
        </Label>
        <Input
          data-testid={`cal-depth-${side}-meters`}
          type="number" step="0.1" min="0.1" max="50"
          value={meters}
          onChange={(e) => setMeters(parseFloat(e.target.value) || 0)}
          className="mt-1 bg-secondary border-border focus:border-primary font-mono"
        />
        <div className="mt-1 font-mono text-[10px] text-muted-foreground">
          WFDF defaults: Outdoor 18 m · Beach 10 m · Indoor 5 m
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <Button variant="outline" size="sm" onClick={() => setPts([])}
          className="border-border bg-secondary hover:bg-accent text-foreground">
          <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset
        </Button>
        <label className="flex items-center gap-2 cursor-pointer text-xs ml-auto">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)}
            className={`w-4 h-4 accent-${color}`} />
          <span className="text-muted-foreground">Diese Linie verwenden</span>
        </label>
      </div>

      <div className="flex gap-2">
        <Button variant="outline" onClick={onBack}
          className="flex-1 border-border bg-secondary hover:bg-accent text-foreground">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        {nextIsSanity ? (
          <Button data-testid={`cal-${side}-next-sanity`} onClick={onNextSanity || onNext}
            disabled={saving || (enabled && (pts.length !== 2 || !meters))}
            className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 font-display font-bold uppercase tracking-widest">
            Sanity Check <ShieldCheck className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button data-testid={`cal-${side}-next`} onClick={onNext}
            disabled={saving || (enabled && (pts.length !== 2 || !meters))}
            className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 font-display font-bold uppercase tracking-widest">
            Next <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        )}
      </div>
    </div>
  );
}

/* ═════════════════ Phase 4 — Sanity / Brick check ═════════════════ */

function SanityPanel({
  sanity, preset, rectL, rectW,
  nearEnabled, nearMeters, farEnabled, farMeters,
  brickMeters, setBrickMeters, brickSide, setBrickSide, onChangeBrick,
  onBack, onConfirm, saving,
  // 🆕 Phase E v8c.2 · Multi-Zone state from parent.
  extraZones = [], onAddZone, onRemoveZone, fmt = "Outdoor",
  // 🆕 Phase E v8c.5 · Coverage silhouette plumbing.
  basePresetId, baseSide, fieldProfile,
  // 🆕 v8c.13c (2026-02-28) · Brick reticle live-drag readout
  // threaded from parent. Surfaces the live (X, Y) m value
  // INSIDE the side panel instead of overlaying it on the canvas.
  liveField = null, dragActive = false,
}) {
  const score = sanity?.score ?? 0;
  const errPx = sanity?.mean_error_px ?? 0;
  const verdict = score >= 90 ? { lbl: "Excellent", color: "text-emerald-400", Icon: ShieldCheck }
                : score >= 70 ? { lbl: "Good", color: "text-emerald-300", Icon: ShieldCheck }
                : score >= 50 ? { lbl: "Acceptable", color: "text-amber-400", Icon: ShieldCheck }
                : score >= 30 ? { lbl: "Suspicious", color: "text-orange-400", Icon: Skull }
                : { lbl: "Likely bad", color: "text-destructive", Icon: Skull };
  const V = verdict.Icon;

  return (
    <div className="border border-border bg-card rounded-sm p-5 space-y-4">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
          Phase 4 · Sanity & Brick-Test
        </div>
        <h3 className="font-display font-black uppercase text-lg tracking-wider">
          Calibration Review
        </h3>
      </div>

      {/* Sanity score */}
      <div className="border border-border bg-secondary/50 rounded-sm p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Sanity Score
          </span>
          <span className={`font-mono text-[10px] uppercase tracking-widest font-bold flex items-center gap-1 ${verdict.color}`}>
            <V className="w-3.5 h-3.5" /> {verdict.lbl}
          </span>
        </div>
        <div className="flex items-baseline gap-2 mb-2">
          <span className={`font-display font-black text-4xl tabular-nums ${verdict.color}`} data-testid="sanity-score">
            {score}
          </span>
          <span className="text-muted-foreground font-mono text-xs">/ 100</span>
        </div>
        <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
          <div className={`h-full transition-all ${score >= 70 ? "bg-emerald-400" : score >= 30 ? "bg-amber-400" : "bg-destructive"}`}
               style={{ width: `${score}%` }} />
        </div>
        <div className="mt-2 font-mono text-[10px] text-muted-foreground tabular-nums">
          Mean re-projection error: <span className="text-foreground">{errPx} px</span>
          {(nearEnabled || farEnabled) && (
            <span className="ml-1 opacity-70">(with {[nearEnabled && "near", farEnabled && "far"].filter(Boolean).join(" + ")} depth)</span>
          )}
        </div>
      </div>

      {/* Brick-Test — visual proof + override */}
      <div className="border border-border bg-secondary/50 rounded-sm p-4">
        <div className="flex items-center justify-between gap-1.5 mb-2">
          <div className="flex items-center gap-1.5">
            <Crosshair className="w-3.5 h-3.5 text-pink-400" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Virtual Brick-Test
            </span>
          </div>
          {/* 🆕 v8c.13c (2026-02-28) · Live (X, Y) m readout pinned
              INSIDE the side panel — no longer floats on the canvas.
              Shows the LIVE drag value while the reticle is being
              moved, otherwise the persisted snapshot from the
              sanity payload. Same testids as before so headless
              tests keep working. */}
          {dragActive && Number.isFinite(liveField?.x) && Number.isFinite(liveField?.y) ? (
            <span
              data-testid="brick-live-drag-xy-badge"
              className="font-mono text-[10px] tabular-nums text-pink-300 px-2 py-0.5 rounded-sm border border-pink-400/40 bg-pink-400/10"
            >
              → ({liveField.x.toFixed(1)}, {liveField.y.toFixed(1)}) m · dragging
            </span>
          ) : sanity?.brick_field_xy ? (
            <span
              data-testid="brick-field-xy-badge"
              className="font-mono text-[10px] tabular-nums text-primary px-2 py-0.5 rounded-sm border border-primary/40 bg-primary/10"
            >
              ({sanity.brick_field_xy.x.toFixed(1)}, {sanity.brick_field_xy.y.toFixed(1)}) m
            </span>
          ) : null}
        </div>
        <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
          Das pinke Fadenkreuz im Video zeigt, wo die Brick-Markierung auf dem
          echten Feld liegen MÜSSTE — wenn es nicht über der echten Markierung
          liegt, ist die Kalibrierung schief. WFDF default: 20 m von der Goalline
          ins Feld hinein. &bdquo;Gegenüber. Endzone&ldquo; projiziert auf den Brick auf
          der anderen Feldhälfte (nahe der entgegengesetzten Goalline).
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Brick-Distanz (m)
            </Label>
            <Input
              data-testid="brick-distance-input"
              type="number" step="0.1" min="0.1" max="50"
              value={brickMeters}
              onChange={(e) => setBrickMeters(parseFloat(e.target.value) || 0)}
              onBlur={(e) => {
                // Pass the freshly-typed value as an explicit override
                // so the sanity preview uses the new meters even if
                // React hasn't re-rendered yet — same closure-stale
                // protection as the side dropdown below.
                const next = parseFloat(e.target.value) || 0;
                onChangeBrick({ brickMetersOverride: next });
              }}
              className="mt-1 bg-secondary border-border focus:border-primary font-mono"
            />
          </div>
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Seite
            </Label>
            <Select
              value={brickSide}
              onValueChange={(v) => {
                setBrickSide(v);
                // Bypass setTimeout + stale-closure: pass the new
                // side as an explicit override so the sanity preview
                // uses the value the coach just clicked, not the
                // value React's previous render captured. Fixes the
                // "Near endzone reports far-mode (18.5, 36)" crosshair
                // inversion bug (2026-02-25).
                onChangeBrick({ brickSideOverride: v });
              }}
            >
              <SelectTrigger data-testid="brick-side-select" className="mt-1 bg-secondary border-border focus:border-primary">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="near" data-testid="brick-side-near">Brick dieser Endzone</SelectItem>
                <SelectItem value="far" data-testid="brick-side-far">Brick der gegenüber. Endzone</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Calibration summary */}
      <div className="border border-border bg-secondary/30 rounded-sm p-3 font-mono text-[10px] text-muted-foreground space-y-1 leading-relaxed">
        <div><span className="text-muted-foreground">Preset:</span> <span className="text-foreground">{preset.labelDe}</span></div>
        <div><span className="text-muted-foreground">Base rectangle:</span> <span className="text-foreground tabular-nums">{rectL} × {rectW} m</span></div>
        {nearEnabled && (
          <div><span className="text-amber-400">Near depth:</span> <span className="text-foreground tabular-nums">{nearMeters} m</span></div>
        )}
        {farEnabled && (
          <div><span className="text-pink-400">Far depth:</span> <span className="text-foreground tabular-nums">{farMeters} m</span></div>
        )}
      </div>

      {/* 🆕 Phase E v8c.2 · Multi-Zone management block — extracted
          into <SanityMultiZoneBlock/> in v8c.5 to land Coverage-
          Silhouette + reduce Step2 LOC under the 1600 cap. */}
      <SanityMultiZoneBlock
        fmt={fmt}
        basePresetId={basePresetId}
        baseSide={baseSide}
        fieldProfile={fieldProfile}
        extraZones={extraZones}
        onAddZone={onAddZone}
        onRemoveZone={onRemoveZone}
      />

      {/* Nav */}
      <div className="flex gap-2">
        <Button data-testid="sanity-back" variant="outline" onClick={onBack}
          className="flex-1 border-border bg-secondary hover:bg-accent text-foreground">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        <Button data-testid={TID.saveCalibrationBtn} onClick={onConfirm} disabled={saving}
          className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 font-display font-bold uppercase tracking-widest">
          <Check className="w-4 h-4 mr-2" /> Confirm
        </Button>
      </div>
    </div>
  );
}

